/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import * as Sentry from '@sentry/browser'

import {
  Spinner,
  SvgIcon,
  Button,
  TextArea,
  Picklist
} from '../'

import {
  readablePhoneNumber,
  normalizePhoneNumber
} from '../../utils'

import {
  CHANNEL_SMS,
  IDENTITY_PICSA_ADVISORY_SERVICES
} from '../../constants'


class SMSSend extends React.PureComponent {
  static propTypes = {
    toIdentityId: PropTypes.string.isRequired,
    fromIdentityId: PropTypes.string,
    relation: PropTypes.shape({
      contentType: PropTypes.number,
      objectId: PropTypes.string
    }),
    message: PropTypes.string,
    onMessageCreateSuccess: PropTypes.func,
    onMessageCreateFail: PropTypes.func,
    onMessageRelationCreateSuccess: PropTypes.func,
    onMessageRelationCreateFail: PropTypes.func,
    onCancelClick: PropTypes.func,
    onChange: PropTypes.func,
    replyToMessage: PropTypes.object,
    sendButtonLabel: PropTypes.string,
    fromAddr: PropTypes.string,
    toAddr: PropTypes.string,
    channelId: PropTypes.string,
    className: PropTypes.string
  }

  static defaultProps = {
    sendButtonLabel: 'Send',
    channelId: CHANNEL_SMS,
    className: ''
  }

  state = {
    toAddr: '',
    messageContent: this.props.message || '',
    sendInProgress: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.identity) {
      this.setDefaultPhoneNumber(nextProps.data.identity)
    }
  }

  setDefaultPhoneNumber = (identity) => {
    if (identity.activePhoneNumbers.edges.length > 0) {
      let defaultPhoneNumberRecord = identity.activePhoneNumbers.edges.find((phoneNumber) => (
        phoneNumber.node.default
      ))
      if (defaultPhoneNumberRecord) {
        this.setState({ toAddr:  readablePhoneNumber(defaultPhoneNumberRecord.node.addressValue) })
      }
    }
  }

  sendSMS = (replyToMessage) => {
    this.setState({ sendInProgress: true })

    let input = {
      channel: this.props.channelId,
      toIdentity: this.props.toIdentityId,
      fromIdentity: this.props.fromIdentityId || IDENTITY_PICSA_ADVISORY_SERVICES,
      direction: "outbound",
      content: this.props.message || this.state.messageContent
      // status - automatically set to 'unsent' initially for 'outbound' messages
    }

    if (this.props.fromAddr) {
      input.fromAddr = normalizePhoneNumber(this.props.fromAddr)
    }

    if (replyToMessage) {
      input.replyTo = replyToMessage.id
      input.toAddr = replyToMessage.fromAddr
    } else {
      input.toAddr = normalizePhoneNumber(this.props.toAddr || this.state.toAddr)
    }
    this.props.createMessage({ variables: { input }})
    .then(({ data }) => {
      if (this.props.onMessageCreateSuccess) {
        this.props.onMessageCreateSuccess(data.messageMutate.message.id)
      }

      this.setState({ messageContent: '', sendInProgress: false })

      if (this.props.relation) {
        this.createMessageRelation(data.messageMutate.message.id)
      }
    })
    .catch(error => {
      Sentry.captureMessage(`Message creation failed - ${error}`)

      this.setState({ sendInProgress: false })

      if (this.props.onMessageCreateFail) {
        this.props.onMessageCreateFail()
      }
    })
  }

  createMessageRelation = (message) => {
    let input = {
      message,
      contentType: this.props.relation.contentType,
      objectId: this.props.relation.objectId
    }
    this.props.createMessageRelation({ variables: { input }})
    .then(({ data }) => {
      if (this.props.onMessageRelationCreateSuccess) {
        this.props.onMessageRelationCreateSuccess()
      }
    })
    .catch(error => {
      Sentry.captureMessage(`Message relation creation failed - ${error}`)
      if (this.props.onMessageRelationCreateFail) {
        this.props.onMessageRelationCreateFail()
      }
    })
  }

  render() {
    const { data, message, replyToMessage, forwardedRef, sendButtonLabel, toAddr, className,
      onChange, onCancelClick } = this.props

    if (toAddr === undefined && replyToMessage === undefined) {
      if (data.loading) {
        return <Spinner className="slds-is-relative" size="small" />
      }

      if (data.error) {
        return (
          <div>
            <SvgIcon
              variant="utility"
              symbol="warning"
              colour="error"
              size="small"
              className="slds-m-left_small slds-m-right_small"
            />
            Fetching identity data failed.
            <Button
              type="reset"
              onClick={() => data.refetch()}
              className="slds-m-left_small">
              Try again
            </Button>
          </div>
        )
      }

      const nonOptedOutActivePhoneNumbers = data.identity.activePhoneNumbers.edges.filter(number => (
        number.node.optedOut === false || number.node.optedOut === null
      ))
      if (nonOptedOutActivePhoneNumbers.length === 0) {
        return (
          <div>
            <SvgIcon
              variant="utility"
              symbol="info"
              colour="warning"
              size="small"
              className="slds-m-left_small slds-m-right_small"
            />
            This contact currently has no valid address for communication in this way.
          </div>
        )
      }
    }

    return (
      <div className={className}>
        {replyToMessage || toAddr
          ? null
          : <div className="slds-box slds-m-bottom_small" style={{ padding: 0, backgroundColor: "white" }}>
              <div className="slds-grid">
                <div className="slds-m-left_x-small slds-m-top_xx-small slds-m-right_medium slds-text-color_weak">
                  To
                </div>
                <Picklist
                  id="toAddr"
                  options={data.identity.activePhoneNumbers.edges.map(phoneNumber => (
                    readablePhoneNumber(phoneNumber.node.addressValue)
                  ))}
                  value={this.state.toAddr}
                  onChange={toAddr => this.setState({ toAddr })}
                  symbol="chevrondown"
                  closeDropdownOnSelect={true}
                  inline={true}
                  className="slds-col"
                />
              </div>
            </div>
        }
        <TextArea
          id='messageContent'
          value={message || this.state.messageContent}
          onChange={e => {
            const messageContent = e.target.value

            if (onChange) {
              onChange(messageContent)
            }

            this.setState({ messageContent })
          }}
          className="fullwidth"
          rows={3}
          ref={forwardedRef}
        />
        <div className="slds-m-top_small">
          {onCancelClick &&
            <Button
              onClick={onCancelClick}
              minWidth={100}>
              Cancel
            </Button>
          }
          <Button
            type="brand"
            onClick={() => this.sendSMS(replyToMessage)}
            minWidth={100}
            className={onCancelClick && "slds-m-left_medium"}
            disabled={message
              ? message.length === 0 || this.state.sendInProgress
              : this.state.messageContent.length === 0 || this.state.sendInProgress
            }>
            {this.state.sendInProgress
              ? 'Sending...'
              : sendButtonLabel
            }
          </Button>
        </div>
      </div>
    )
  }
}

const getIdentityActivePhoneNumbers = gql`
  query($id: ID!, $notRequiredToRunQuery: Boolean!) {
    identity(id: $id) @skip(if: $notRequiredToRunQuery) {
      id
      activePhoneNumbers: addresses(addressType: "msisdn", inactive: false) {
        edges {
          node {
            id
            addressValue
            default
            optedOut
          }
        }
      }
    }
  }
`

const createMessage = gql`
  mutation createMessage($input: MessageMutationInput!) {
    messageMutate(input: $input) {
      message {
        ...Message
      }
    }
  }
  ${fragments.message}
`

const createMessageRelation = gql`
  mutation createMessageRelation($input: MessageRelationMutationInput!) {
    messagerelationMutate(input: $input) {
      messagerelation {
        id
        message {
          id
        }
        contentType {
          id
        }
        objectId
      }
    }
  }
`

const withData =
  graphql(getIdentityActivePhoneNumbers, {
    options: ({ toIdentityId, toAddr, replyToMessage }) => ({
      variables: {
        id: toIdentityId,
        notRequiredToRunQuery: toAddr !== undefined || replyToMessage !== undefined
      }
    })
  })

const withMessageMutate =
  graphql(createMessage, {
    name: "createMessage"
  })

const withMessageRelationMutate =
  graphql(createMessageRelation, {
    name: "createMessageRelation"
  })

export default compose(
  withData,
  withMessageMutate,
  withMessageRelationMutate
)(SMSSend)



// WEBPACK FOOTER //
// ./src/components/sms-send/index.js